import type { PieceData } from '../types';
import { SONATA_FILES } from './catalog.generated';
import { VARIATION_FILES } from '../variations/catalog.generated';
import { WTC_FILES } from '../wtc/catalog.generated';
import { PIANO_FILES } from '../piano/catalog.generated';
import { decodeNotes } from '../chorales/codec';
import { DerivedSonata, deriveSonata } from './derive';
import { SONATA_EDITORIAL } from './editorial';
import type { SonataFile } from './types';
import type { NoteEvent } from '../../music/piece';

// Sonata movements (DCML, non-commercial) and variation sets (TAVERN, CC BY-SA) share the format
// and the derivation; they are kept in two catalogues so that a build can leave one out.
const FILES = new Map<string, SonataFile>([...SONATA_FILES, ...VARIATION_FILES, ...WTC_FILES, ...PIANO_FILES].map((f) => [f.id, f]));

/** Sonata movements in the atlas, in Köchel order. */
export const SONATA_IDS: string[] = SONATA_FILES.map((f) => f.id);

/** Theme-and-variations sets: Mozart, then Beethoven. */
export const VARIATION_IDS: string[] = VARIATION_FILES.map((f) => f.id);

/** Preludes 2–24 of The Well-Tempered Clavier, Book I (Prelude 1 is the hand-annotated BWV 846). */
export const PRELUDE_IDS: string[] = WTC_FILES.map((f) => f.id);

/** Later piano music imported from MIDI and read by rule (Sibelius). */
export const PIANO_IDS: string[] = PIANO_FILES.map((f) => f.id);

export function sonataFile(id: string): SonataFile | undefined {
  return FILES.get(id);
}

const cache = new Map<string, DerivedSonata>();
/** Derived lazily and cached; editorial additions applied on top. */
export function derivedSonata(id: string): DerivedSonata | undefined {
  let d = cache.get(id);
  if (!d) {
    const f = FILES.get(id);
    if (!f) return undefined;
    d = deriveSonata(f);
    const edit = SONATA_EDITORIAL[id];
    if (edit) d = { ...d, piece: edit(d.piece) };
    cache.set(id, d);
  }
  return d;
}

export function sonataPiece(id: string): PieceData | undefined {
  return derivedSonata(id)?.piece;
}

export function sonataNotes(id: string): NoteEvent[] | undefined {
  const f = FILES.get(id);
  return f ? decodeNotes(f) : undefined;
}

export function allSonatas(): DerivedSonata[] {
  return SONATA_IDS.map((id) => derivedSonata(id)!);
}

export function allVariations(): DerivedSonata[] {
  return VARIATION_IDS.map((id) => derivedSonata(id)!);
}

export function allPiano(): DerivedSonata[] {
  return PIANO_IDS.map((id) => derivedSonata(id)!);
}

export function allPreludes(): DerivedSonata[] {
  return PRELUDE_IDS.map((id) => derivedSonata(id)!);
}
